// The cloud machine coming up, as Getting ready and the Windows app's Magic Setup show it (the Mac's SetupLife):
// where it is in its life, its label, and how far along Make · Start · Ready it is, polled from the hub.

import { useEffect, useState } from 'react';
import { machineState, type MachineLife } from '../api';

export type Life = MachineLife | 'none' | 'full' | 'reserved' | 'unknown';

export const LIFE: Record<Life, { label: string; at: number }> = {
  none: { label: 'On its way', at: 0.08 },
  unknown: { label: 'Checking', at: 0.08 },
  full: { label: 'Waiting for room', at: 0.08 },
  reserved: { label: 'Reserving', at: 0.08 },
  creating: { label: 'Making it', at: 0.34 },
  booting: { label: 'Starting', at: 0.72 },
  rebooting: { label: 'Restarting', at: 0.72 },
  stopped: { label: 'Stopped', at: 0.72 },
  error: { label: 'Needs a look', at: 0.72 },
  unreachable: { label: 'Can’t reach it', at: 0.72 },
  ready: { label: 'Ready', at: 1 },
};

/** The machine's life and id, every 2 s while it comes up, every 15 s once it's ready. Only a 404 means no machine:
 *  a failed ask (the hub slow, a network error) keeps what was known and asks again; a word this page doesn't know yet
 *  (a newer hub's) reads as Checking. */
export function useMachineLife(active: boolean): { life: Life; id: string | null } {
  const [s, setS] = useState<{ life: Life; id: string | null }>({ life: 'unknown', id: null });
  useEffect(() => {
    if (!active) return;
    let alive = true;
    let t: ReturnType<typeof setTimeout>;
    const poll = async () => {
      const r = await machineState();
      if (!alive) return;
      if (r.ok) setS({ life: r.data.machine.state in LIFE ? r.data.machine.state : 'unknown', id: r.data.machine.id });
      else if (r.status === 404) setS({ life: r.body?.reserved ? 'reserved' : 'none', id: null });
      const done = r.ok && r.data.machine.state === 'ready';
      t = setTimeout(poll, done ? 15000 : 2000);
    };
    void poll();
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [active]);
  return s;
}
